import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { Button as MuiButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Tooltip } from '@ui/Tooltip';
import { ghostButtonLabelStyles } from './ghost-button.styles';

type GhostButtonProps = Pick<
  React.AriaAttributes,
  'aria-label' | 'aria-pressed'
> & {
  children: ReactNode;
  startIcon?: ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  variant?: 'default' | 'primary';
  size?: 'default' | 'large';
  fullWidth?: boolean;
  /** Fixed width. The label truncates and the full text shows in a tooltip. */
  width?: number | string;
  className?: string;
};

export const GhostButton = ({
  children,
  startIcon,
  onClick,
  disabled,
  variant = 'default',
  size = 'default',
  fullWidth,
  width,
  className,
  ...ariaProps
}: GhostButtonProps) => {
  const constrained = fullWidth === true || width !== undefined;
  const { classes, cx } = useStyles({ constrained });
  const labelRef = useRef<HTMLSpanElement>(null);
  const [tooltipTitle, setTooltipTitle] = useState('');

  useLayoutEffect(() => {
    const label = labelRef.current;
    if (!label || !constrained) {
      setTooltipTitle('');
      return;
    }
    const text = label.textContent ?? '';
    const truncated = label.scrollWidth > label.clientWidth;
    setTooltipTitle(truncated ? text : '');
  }, [children, constrained, width, fullWidth]);

  const button = (
    <MuiButton
      {...ariaProps}
      variant={variant === 'primary' ? 'contained' : 'text'}
      color="primary"
      size={size === 'large' ? 'large' : 'small'}
      disableElevation
      disabled={disabled}
      fullWidth={fullWidth}
      onClick={onClick}
      startIcon={startIcon}
      className={cx(classes.root, 'GhostButton-root', className)}
      style={width === undefined ? undefined : { width }}
    >
      <span
        ref={labelRef}
        className={cx(classes.label, size === 'large' && classes.largeLabel)}
      >
        {children}
      </span>
    </MuiButton>
  );

  if (!tooltipTitle) return button;

  return (
    <Tooltip title={tooltipTitle}>
      <span className={classes.tooltipAnchor}>{button}</span>
    </Tooltip>
  );
};

const useStyles = makeStyles<{ constrained: boolean }>()(
  (_theme, { constrained }) => ({
    root: {
      display: 'inline-flex',
      flexShrink: constrained ? 1 : 0,
      width: constrained ? undefined : 'max-content',
      minWidth: constrained ? 0 : 'auto',
      padding: '0 8px',
      whiteSpace: 'nowrap',
      '& .MuiButton-startIcon': {
        marginLeft: 0,
        marginRight: 0,
      },
    },
    label: {
      ...ghostButtonLabelStyles,
      padding: '0 4px',
    },
    largeLabel: { padding: '0 6px' },
    tooltipAnchor: {
      display: 'inline-flex',
      minWidth: 0,
      maxWidth: '100%',
    },
  }),
);
